import { useState } from "react";
import { useCreateDocumentationAreaActorInvitation } from "src/apps/hooks/useCreateDocumentationAreaActorInvitation";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { useNotifications } from "src/apps/hooks/useNotifications";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { NotificationStyles } from "src/contexts/_shared/domain/NotificationStyles";
import { createActorAccessNotification } from "src/apps/hooks/createActorAccessNotification";

const MODAL_TITLE = "documentation_area.actor_invitation.modal.title";
const INVALID_EMAIL_ERROR = "common.error.invalid_email";
const GENERIC_ERROR = "common.error.generic";
const SUCCESS_TITLE = "documentation_area.actor_invitation.success.title";
const SUCCESS_MESSAGE = "documentation_area.actor_invitation.success.message";

const ERROR_CODE_MAP: Record<string, string> = {
  INVALID_EMAIL: INVALID_EMAIL_ERROR,
};

export const useActorInvitationForm = (onClose: () => void) => {
  const { selectedActorId, documentationArea } = useDocumentationArea();
  const { createActorInvitation, isPending } = useCreateDocumentationAreaActorInvitation();
  const { addNotifications } = useNotifications();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const actorId = selectedActorId ?? documentationArea?.getActor().getId();

  const resetAndClose = () => {
    setEmail("");
    setEmailError("");
    onClose();
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
    setEmailError("");
  };

  const submit = () => {
    if (!actorId) return;
    if (!isEmailValid(email)) {
      setEmailError(INVALID_EMAIL_ERROR);
      return;
    }

    createActorInvitation(
      { email: email.trim(), actorId },
      {
        onSuccess: () => {
          addNotifications([
            createActorAccessNotification(
              SUCCESS_TITLE,
              SUCCESS_MESSAGE,
              NotificationStyles.SUCCESS,
            ),
          ]);
          resetAndClose();
        },
        onError: (error) => {
          let message = GENERIC_ERROR;
          if (error instanceof HTTPError) {
            const code = error.getCode();
            if (code && ERROR_CODE_MAP[code]) message = ERROR_CODE_MAP[code];
          }

          addNotifications([
            createActorAccessNotification(MODAL_TITLE, message, NotificationStyles.ALERT),
          ]);
          resetAndClose();
        },
      },
    );
  };

  return { email, emailError, isPending, onEmailChange, resetAndClose, submit };
};

const isEmailValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
